import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, apiUpload, useAuth } from '@shop/api-client'
import type { Brand, Category, Product, TaxGroup } from '../api/types'
import { Perm } from '../api/types'
import { toNumber } from '../ui/format'
import { ImageField } from '../ui/ImageField'
import {
  Banner,
  Button,
  Empty,
  ErrorBanner,
  Field,
  Panel,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '@shop/ui'

/**
 * إدخالُ مادّةٍ مباشرةً — <b>بكامل تفاصيلها وبلا مسوّدة</b>.
 *
 * <b>ولماذا إلى جانب الإدخال الذكيّ لا بدلًا منه:</b> المسوّدةُ طريقُ من لا
 * يعرف الصنفَ بعد — يُملي ما سمعه أو يمسح ما بيده، فيُحلَّل ويُطابَق ويُراجَع.
 * وهذه الشاشةُ طريقُ من يعرف: صنفٌ في يده وورقتُه أمامه، فيكتب كلَّ شيءٍ مرّةً
 * واحدةً ويحفظ. وإجبارُه على المسوّدة يعني خطوتين وشاشتين لِما يعرفه سلفًا.
 *
 * ⚠ <b>والتكرارُ يُسأل عنه قبل الحفظ لا بعده.</b> تنبيهٌ يأتي بعد أن يُنشأ
 *   الصنفُ تنبيهٌ متأخّر: صار في الكتالوج صنفان لمادّةٍ واحدة، ودمجُهما لاحقًا
 *   عملٌ يدويٌّ لا يقوم به أحد. ولذلك يُنادى `check-duplicates` عند ترك حقلِ
 *   الاسم، ويُعرض ما وُجد قبل أن يُضغط الحفظ.
 *
 * ⚠ <b>ولا يدمج النظامُ شيئًا من تلقاء نفسه.</b> المانعُ (باركودٌ مستعمَل) يوقف
 *   الحفظ لأنّه خطأٌ قطعًا — الرقمُ فريدٌ في القاعدة. وغيرُ المانع (اسمٌ شبيه)
 *   يمرّ بإقرارٍ صريحٍ من المُدخِل، وهو قرارُ إنسانٍ يرى الصنفين.
 */
export default function ProductNew() {
  const navigate = useNavigate()
  const { can } = useAuth()

  // ── المنتج ───────────────────────────────────────────────────────────────
  const [nameAr, setNameAr] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [brandId, setBrandId] = useState('')
  const [model, setModel] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [taxGroupId, setTaxGroupId] = useState('')
  const [description, setDescription] = useState('')
  const [warrantyMonths, setWarrantyMonths] = useState('')
  const [warrantyNote, setWarrantyNote] = useState('')

  // ── المتغيّرُ الأوّل ───────────────────────────────────────────────────────
  const [variantName, setVariantName] = useState('')
  const [modelNumber, setModelNumber] = useState('')
  const [colour, setColour] = useState('')
  const [capacity, setCapacity] = useState('')
  const [requiresSerial, setRequiresSerial] = useState(false)
  const [requiresImei, setRequiresImei] = useState(false)

  // باركوداتٌ عدّة: للمادّة الواحدة علبةُ المصنع وملصقُ المحلّ، وأوّلُها الرئيسيّ.
  const [barcodes, setBarcodes] = useState<string[]>([])
  const [barcodeDraft, setBarcodeDraft] = useState('')

  const [sellPrice, setSellPrice] = useState('')
  const [image, setImage] = useState<File | null>(null)

  const [acknowledge, setAcknowledge] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const brands = useQuery({
    queryKey: ['brands'],
    queryFn: () => api<Brand[]>('/api/v1/catalog/brands'),
  })

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<Category[]>('/api/v1/catalog/categories'),
  })

  const taxGroups = useQuery({
    queryKey: ['tax-groups'],
    queryFn: () => api<TaxGroup[]>('/api/v1/catalog/tax-groups'),
  })

  // ── كشفُ التكرار ─────────────────────────────────────────────────────────
  const duplicates = useMutation({
    mutationFn: () =>
      api<DuplicateHint[]>('/api/v1/products/check-duplicates', {
        method: 'POST',
        body: {
          nameAr: nameAr.trim() || null,
          brandId: brandId === '' ? null : brandId,
          model: model.trim() || null,
          barcodes: barcodes.length > 0 ? barcodes : null,
          modelNumbers: modelNumber.trim() === '' ? null : [modelNumber.trim()],
        },
      }),
  })

  const hints = duplicates.data ?? []
  const blocking = hints.filter((hint) => hint.isBlocking)
  const similar = hints.filter((hint) => !hint.isBlocking)

  const probe = () => {
    if (nameAr.trim() === '') {
      return
    }

    setAcknowledge(false)
    duplicates.mutate()
  }

  const months = warrantyMonths.trim() === '' ? null : Number(warrantyMonths.trim())
  const monthsBad = months !== null && (!Number.isInteger(months) || months < 0 || months > 600)
  const price = sellPrice.trim() === '' ? null : toNumber(sellPrice)
  const priceBad = sellPrice.trim() !== '' && (price === null || price < 0)

  const addBarcode = () => {
    const value = barcodeDraft.trim()

    if (value === '' || barcodes.includes(value)) {
      setBarcodeDraft('')

      return
    }

    setBarcodes((current) => [...current, value])
    setBarcodeDraft('')
    setAcknowledge(false)
  }

  const save = useMutation({
    mutationFn: async () => {
      const created = await api<Product>('/api/v1/products', {
        method: 'POST',
        body: {
          nameAr: nameAr.trim(),
          nameEn: nameEn.trim() || null,
          brandId: brandId === '' ? null : brandId,
          model: model.trim() || null,
          categoryId,
          taxGroupId: taxGroupId === '' ? null : taxGroupId,
          description: description.trim() || null,
          warrantyMonths: months,
          warrantyNote: warrantyNote.trim() || null,
          acknowledgeDuplicates: acknowledge,

          // ⚠ والمتغيّرُ يُرسل مع المنتج في النداء نفسِه — لا نداءان: بينهما
          //   منتجٌ بلا شيءٍ يُباع، وإن سقط الاتّصالُ بقي كذلك إلى الأبد.
          //   وشكلُ الرسالة يجعل كسرَ ذلك غيرَ ممكن.
          variants: [
            {
              variantName: variantName.trim() || null,
              modelNumber: modelNumber.trim() || null,
              colour: colour.trim() || null,
              capacity: capacity.trim() || null,
              requiresSerial: requiresSerial || requiresImei,
              requiresImei,
              isDefault: true,
              barcodes: barcodes.map((barcode, index) => ({
                barcode,
                isPrimary: index === 0,
              })),
            },
          ],
        },
      })

      // ⚠ وما بعد الإنشاء لا يُسقطه: الصنفُ حُفظ ومعه متغيّرُه وباركوداتُه.
      //   وصورةٌ لم تصعد أو سعرٌ لم يُوضع يُستأنفان من شاشة الصنف بضغطة،
      //   بخلاف نموذجٍ امتلأ بعشرين حقلًا ثمّ أُفرغ لأنّ نداءً ثانيًا سقط.
      const variantId = created.variants[0]?.id

      if (image !== null) {
        try {
          await apiUpload(`/api/v1/products/${created.id}/image`, image)
        } catch {
          // تُرى في شاشة الصنف: لا صورة.
        }
      }

      if (price !== null && variantId && can(Perm.priceEdit)) {
        try {
          await api(`/api/v1/variants/${variantId}/price`, {
            method: 'PUT',
            body: { price, currency: 'JOD', reason: 'إدخالٌ مباشر' },
          })
        } catch {
          // يُوضع من شاشة الصنف.
        }
      }

      return created
    },
    onSuccess: (created) => {
      setError(null)
      navigate(`/products/${created.id}`)
    },
    onError: setError,
  })

  if (!can(Perm.productCreate)) {
    return <Empty>لا تملك صلاحيّةَ إنشاء الأصناف.</Empty>
  }

  const busy = save.isPending
  const needsAck = similar.length > 0 && !acknowledge
  // ⚠ **ولا حفظَ بلا قسم** — قرارُ المالك. والخادمُ يرفض بـ
  //   `catalog.category_required` على أيّ حال؛ وهذا يمنع الوصولَ إلى رفضه.
  const ready =
    nameAr.trim() !== '' && categoryId !== ''
    && !monthsBad && !priceBad && blocking.length === 0 && !needsAck

  return (
    <>
      <Panel title="مادّةٌ جديدة — إدخالٌ مباشر">
        <Banner tone="info">
          للصنف الذي بين يديك وتعرف تفاصيلَه. وما لا تعرفه أملِه من
          <strong> الإدخال الذكيّ</strong> ليُحلَّل ويُطابَق قبل أن يصير صنفًا.
        </Banner>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="الاسم بالعربيّة" hint="يُترك ويُفحص التكرارُ تلقائيًّا.">
            <TextInput value={nameAr} onChange={setNameAr} disabled={busy} autoFocus />
          </Field>

          <Field label="الاسم بالإنجليزيّة">
            <TextInput value={nameEn} onChange={setNameEn} dir="ltr" disabled={busy} />
          </Field>

          <Field label="الماركة">
            <Select value={brandId} onChange={setBrandId} disabled={busy}>
              <option value="">بلا ماركة</option>
              {(brands.data ?? []).filter((b) => b.isActive).map((brand) => (
                <option key={brand.id} value={brand.id}>{brand.nameAr}</option>
              ))}
            </Select>
          </Field>

          <Field label="الطراز" hint="مع الماركة يصيران قاعدةَ كشف التكرار.">
            <TextInput value={model} onChange={setModel} dir="ltr" disabled={busy} />
          </Field>

          <Field
            label="القسم"
            hint="عائلةُ المادّة — وتَرِثُها متغيّراتُها كلُّها. إلزاميّ."
          >
            <Select value={categoryId} onChange={setCategoryId} disabled={busy}>
              <option value="">اختر القسم…</option>
              {(categories.data ?? []).filter((c) => c.isActive).map((category) => (
                <option key={category.id} value={category.id}>{category.nameAr}</option>
              ))}
            </Select>
          </Field>

          <Field label="المجموعة الضريبيّة">
            <Select value={taxGroupId} onChange={setTaxGroupId} disabled={busy}>
              <option value="">الافتراضيّة</option>
              {(taxGroups.data ?? []).map((group) => (
                <option key={group.id} value={group.id}>
                  {group.nameAr} · {group.rate}%
                </option>
              ))}
            </Select>
          </Field>

          <Field label="الكفالة بالأشهر">
            <TextInput value={warrantyMonths} onChange={setWarrantyMonths} dir="ltr" disabled={busy} />
          </Field>

          <Field label="ملاحظةُ الكفالة">
            <TextInput value={warrantyNote} onChange={setWarrantyNote} disabled={busy} />
          </Field>
        </div>

        <div className="mt-3">
          <Field label="الوصف">
            <TextArea value={description} onChange={setDescription} rows={2} disabled={busy} />
          </Field>
        </div>

        <div className="mt-3">
          <Button onClick={probe} disabled={busy || nameAr.trim() === ''}>
            {duplicates.isPending ? 'يُفحص…' : 'افحص التكرار'}
          </Button>
        </div>
      </Panel>

      {(blocking.length > 0 || similar.length > 0) && (
        <Panel title="أصنافٌ شبيهة">
          {blocking.length > 0 && (
            <Banner tone="danger">
              <strong>مانع:</strong> رقمٌ من هذه الأرقام مستعمَلٌ لصنفٍ آخر. الأرقامُ
              فريدةٌ في المحلّ كلِّه — فامسحه من القائمة أو صحّحه.
            </Banner>
          )}

          <ul className="mt-2 space-y-1 text-sm">
            {hints.map((hint) => (
              <li
                key={`${hint.productId}-${hint.matched}`}
                className="flex flex-wrap justify-between gap-2 border-b border-line py-1"
              >
                <span>{hint.productName}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">
                  {hint.productCode} · {hint.matched}
                </span>
                <span className={hint.isBlocking ? 'text-xs text-danger' : 'text-xs text-ink-soft'}>
                  {hint.isBlocking ? 'مانع' : 'تشابه'}
                </span>
              </li>
            ))}
          </ul>

          {similar.length > 0 && blocking.length === 0 && (
            <label className="mt-3 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-brand"
                checked={acknowledge}
                disabled={busy}
                onChange={(event) => setAcknowledge(event.target.checked)}
              />
              <span>
                رأيتُ الشبيهَ وهذا صنفٌ آخر.
                <span className="block text-xs text-ink-soft">
                  ولا يدمج النظامُ شيئًا: الدمجُ قرارُ من يرى الصنفين.
                </span>
              </span>
            </label>
          )}
        </Panel>
      )}

      <Panel title="المتغيّر">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="اسمُ المتغيّر" hint="يُترك فارغًا إن كان الصنفُ واحدًا.">
            <TextInput value={variantName} onChange={setVariantName} disabled={busy} />
          </Field>

          <Field label="رقمُ الصانع">
            <TextInput value={modelNumber} onChange={setModelNumber} dir="ltr" disabled={busy} />
          </Field>

          <Field label="اللون">
            <TextInput value={colour} onChange={setColour} disabled={busy} />
          </Field>

          <Field label="السعة">
            <TextInput value={capacity} onChange={setCapacity} disabled={busy} />
          </Field>
        </div>

        <div className="mt-3 space-y-2">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-brand"
              checked={requiresImei}
              disabled={busy}
              onChange={(event) => {
                setRequiresImei(event.target.checked)

                if (event.target.checked) {
                  setRequiresSerial(true)
                }
              }}
            />
            <span>يتطلّب IMEI <span className="block text-xs text-ink-soft">للهواتف — ويستلزم التسلسلَ معه.</span></span>
          </label>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-brand disabled:opacity-60"
              checked={requiresSerial}
              disabled={busy || requiresImei}
              onChange={(event) => setRequiresSerial(event.target.checked)}
            />
            <span>يتطلّب رقمَ تسلسل</span>
          </label>
        </div>
      </Panel>

      <Panel title="الباركودات">
        {barcodes.length === 0 ? (
          <p className="text-xs text-ink-soft">لا باركود. تُضاف الأرقامُ هنا أو لاحقًا من شاشة الصنف.</p>
        ) : (
          <ul className="space-y-1">
            {barcodes.map((barcode, index) => (
              <li key={barcode} className="flex items-center justify-between gap-2 text-sm">
                <span className="tabular" dir="ltr">
                  {barcode}
                  {index === 0 && (
                    <span className="mr-2 rounded bg-brand/10 px-1.5 py-0.5 text-xs text-brand">رئيسيّ</span>
                  )}
                </span>
                <Button
                  disabled={busy}
                  onClick={() => {
                    setBarcodes((current) => current.filter((c) => c !== barcode))
                    setAcknowledge(false)
                  }}
                >
                  احذف
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-3 flex items-end gap-2">
          <div className="flex-1">
            <Field
              label="أضف باركودًا"
              hint="امسحه بالماسح أو اكتبه. والأوّلُ هو الرئيسيُّ الذي يُطبع."
            >
              <TextInput
                value={barcodeDraft}
                onChange={setBarcodeDraft}
                dir="ltr"
                disabled={busy}
                onKeyDown={(event) => {
                  // الماسحُ لوحةُ مفاتيحَ يُنهي بـEnter: يُضاف الرقمُ ولا
                  // يُرسَل النموذج.
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    addBarcode()
                  }
                }}
              />
            </Field>
          </div>
          <Button onClick={addBarcode} disabled={busy || barcodeDraft.trim() === ''}>أضف</Button>
        </div>
      </Panel>

      <Panel title="الصورة والسعر">
        <ImageField
          path={null}
          busy={busy}
          onPick={setImage}
          onRemove={() => setImage(null)}
          hint="تُرفع بعد حفظ الصنف."
        />

        {can(Perm.priceEdit) && (
          <div className="mt-3 max-w-xs">
            <Field label="سعرُ البيع" hint="يُترك فارغًا فيُوضع لاحقًا.">
              <TextInput value={sellPrice} onChange={setSellPrice} dir="ltr" disabled={busy} />
            </Field>
          </div>
        )}

        {priceBad && <Banner tone="warn">السعرُ رقمٌ غيرُ سالب.</Banner>}
        {monthsBad && <Banner tone="warn">الكفالةُ عددُ أشهرٍ صحيحٌ بين صفرٍ وستّمئة.</Banner>}
      </Panel>

      <ErrorBanner error={error} />
      {save.isPending && <Spinner label="يُحفظ الصنف…" />}

      <div className="flex flex-wrap items-center gap-3">
        <Button tone="primary" disabled={busy || !ready} onClick={() => save.mutate()}>
          {busy ? 'يُحفظ…' : 'احفظ الصنف'}
        </Button>

        {nameAr.trim() === '' && (
          <span className="text-xs text-ink-soft">الاسمُ بالعربيّة مطلوب.</span>
        )}
        {needsAck && (
          <span className="text-xs text-ink-soft">أقِرَّ بأنّك رأيتَ الشبيهَ قبل الحفظ.</span>
        )}
      </div>
    </>
  )
}

/** شبيهٌ وُجد — الشكلُ نفسُه الذي ترسله `check-duplicates`. */
type DuplicateHint = {
  productId: string
  productCode: string
  productName: string
  variantId: string | null
  sku: string | null
  matchKind: string
  isBlocking: boolean
  score: number
  matched: string
}
